import { useEffect, useRef, useState } from 'react';
import { gameSounds } from '../lib/gameSounds';

/**
 * Pass 28ad — shared wild-encounter attack FX layer.
 *
 * Reads the engine's recentAttackFx ring buffer, dedupes by id, and
 * plays a ~1.2s animation per new entry. Misses spawn a floating
 * "MISS" tag; dodges spawn "DODGE". Hits trigger element-specific
 * fullscreen visuals (snow + shake for ice, embers for fire, etc.).
 *
 * Originally lived inside HypeEggWidget; lifted out so the panel
 * variant (HypeEggPanelWidget) can reuse the same drama without
 * duplicating ~300 lines of FX bookkeeping.
 *
 * All effects are pointerEvents:none so they don't intercept clicks
 * on chat — important since OBS captures the browser layer above the
 * stream feed and any click-through would be a regression.
 */

export interface WildFxEntry {
  id: string;
  attackerViewer: string;
  attackId: string;
  attackName: string;
  attackEmoji: string;
  element: string;
  outcome: 'hit' | 'miss' | 'dodge' | 'support' | 'catch_fail';
  damage: number;
  effectiveness?: 'super' | 'normal' | 'resisted';
  ts: number;
  /** Pass 28ap — present on support outcomes. Drives which support
   *  visual to paint (DoT smoke, stun banner, heal flash, etc.). */
  supportKind?: 'dot' | 'stun' | 'group_heal' | 'group_hot' | 'group_buff';
}

export const WILD_FX_KEYFRAMES = `
@keyframes wildFxShake {
  0%, 100% { transform: translate(-50%, -50%); }
  10%, 30%, 50%, 70%, 90% { transform: translate(calc(-50% - 8px), calc(-50% - 4px)); }
  20%, 40%, 60%, 80% { transform: translate(calc(-50% + 8px), calc(-50% + 4px)); }
}
@keyframes wildFxShakeHard {
  0%, 100% { transform: translate(-50%, -50%); }
  10%, 30%, 50%, 70%, 90% { transform: translate(calc(-50% - 14px), calc(-50% - 8px)); }
  20%, 40%, 60%, 80% { transform: translate(calc(-50% + 14px), calc(-50% + 8px)); }
}
@keyframes wildFxFlash {
  0% { opacity: 0; }
  10% { opacity: 0.85; }
  60% { opacity: 0.3; }
  100% { opacity: 0; }
}
@keyframes wildFxFallDown {
  0% { transform: translateY(-100vh) rotate(0deg); opacity: 0; }
  10% { opacity: 1; }
  100% { transform: translateY(100vh) rotate(360deg); opacity: 0; }
}
@keyframes wildFxRiseUp {
  0% { transform: translateY(40vh) scale(0.6); opacity: 0; }
  20% { opacity: 1; }
  100% { transform: translateY(-30vh) scale(1.2); opacity: 0; }
}
@keyframes wildFxRipple {
  0% { transform: translate(-50%, -50%) scale(0.2); opacity: 0.8; }
  100% { transform: translate(-50%, -50%) scale(3.2); opacity: 0; }
}
@keyframes wildFxSwirl {
  0% { transform: rotate(0deg) scale(0.4); opacity: 0; }
  20% { opacity: 1; }
  100% { transform: rotate(540deg) scale(1.5); opacity: 0; }
}
@keyframes wildFxStreak {
  0% { transform: translateX(-100vw); opacity: 0; }
  20% { opacity: 1; }
  100% { transform: translateX(100vw); opacity: 0; }
}
@keyframes wildFxBlinkMiss {
  0% { transform: translate(-50%, 0) scale(0.5); opacity: 0; }
  20% { transform: translate(-50%, -20px) scale(1.05); opacity: 1; }
  80% { transform: translate(-50%, -60px) scale(1); opacity: 1; }
  100% { transform: translate(-50%, -100px) scale(1); opacity: 0; }
}
@keyframes wildFxStrobe {
  0%, 100% { opacity: 0; }
  10%, 30%, 50%, 70%, 90% { opacity: 0.9; }
  20%, 40%, 60%, 80% { opacity: 0.2; }
}
@keyframes wildFxGlitchSlide {
  0% { transform: translateX(0); }
  20% { transform: translateX(-6px); }
  40% { transform: translateX(8px); }
  60% { transform: translateX(-4px); }
  80% { transform: translateX(5px); }
  100% { transform: translateX(0); }
}
@keyframes wildFxPanelShake {
  0%, 100% { transform: translate(0, 0); }
  10%, 50%, 90% { transform: translate(-10px, -3px); }
  30%, 70% { transform: translate(10px, 3px); }
}
@keyframes wildFxPanelShakeHard {
  0%, 100% { transform: translate(0, 0); }
  10%, 50%, 90% { transform: translate(-18px, -6px); }
  30%, 70% { transform: translate(18px, 6px); }
}
`;

export const ELEMENT_FX: Record<
  string,
  {
    flash: string;
    shake: 'none' | 'shake' | 'shakeHard';
    particles: string[];
    particleDir: 'fall' | 'rise' | 'streak' | 'swirl' | 'ripple' | 'none';
  }
> = {
  fire: {
    flash: 'rgba(249,115,22,0.35)',
    shake: 'shake',
    particles: ['🔥', '🔥', '✨'],
    particleDir: 'rise',
  },
  water: {
    flash: 'rgba(56,189,248,0.35)',
    shake: 'none',
    particles: ['💧', '💦', '💧'],
    particleDir: 'ripple',
  },
  grass: {
    flash: 'rgba(34,197,94,0.30)',
    shake: 'shake',
    particles: ['🌿', '🍃', '🌱'],
    particleDir: 'swirl',
  },
  earth: {
    flash: 'rgba(120,72,30,0.35)',
    shake: 'shakeHard',
    particles: ['🪨', '🪨', '💢'],
    particleDir: 'fall',
  },
  electric: {
    flash: 'rgba(250,204,21,0.55)',
    shake: 'shake',
    particles: ['⚡', '⚡', '🌩️'],
    particleDir: 'streak',
  },
  ice: {
    flash: 'rgba(186,230,253,0.30)',
    shake: 'shake',
    particles: ['❄️', '❄️', '🌨️'],
    particleDir: 'fall',
  },
  wind: {
    flash: 'rgba(226,232,240,0.25)',
    shake: 'none',
    particles: ['💨', '🌪️', '💨'],
    particleDir: 'streak',
  },
  shadow: {
    flash: 'rgba(15,15,30,0.55)',
    shake: 'shake',
    particles: ['🌑', '🕳️', '⬛'],
    particleDir: 'swirl',
  },
  light: {
    flash: 'rgba(255,255,255,0.55)',
    shake: 'none',
    particles: ['✨', '☀️', '💡'],
    particleDir: 'ripple',
  },
  spirit: {
    flash: 'rgba(167,139,250,0.40)',
    shake: 'shake',
    particles: ['👻', '💜', '🔮'],
    particleDir: 'rise',
  },
  hype: {
    flash: 'rgba(244,114,182,0.40)',
    shake: 'shakeHard',
    particles: ['🤘', '💥', '🎉'],
    particleDir: 'rise',
  },
  chaos: {
    flash: 'rgba(217,70,239,0.40)',
    shake: 'shakeHard',
    particles: ['🌀', '🔀', '🎲'],
    particleDir: 'swirl',
  },
  glitch: {
    flash: 'rgba(168,85,247,0.40)',
    shake: 'shake',
    particles: ['📺', '🟪', '💥'],
    particleDir: 'streak',
  },
};

export interface WildAttackFxLayerProps {
  recentFx: WildFxEntry[];
  /** When true, the centered shake rect is rendered (matches the
   *  octagon footprint). The panel variant uses its own panel-shake
   *  via the onShake callback and disables this. */
  enableCenterShake?: boolean;
  /** When true, the centered stamp ("MISS" / damage) is shown over
   *  screen center. Panel variant keeps this on so the stream still
   *  reads the hype at-a-glance. */
  enableCenterStamp?: boolean;
  /** Optional callback fired with the strength tier when a hit lands,
   *  so a host widget can shake itself in sync with the FX. */
  onShake?: (tier: 'shake' | 'shakeHard') => void;
}

export function WildAttackFxLayer({
  recentFx,
  enableCenterShake = true,
  enableCenterStamp = true,
  onShake,
}: WildAttackFxLayerProps) {
  // Refs (not state) so adding a new entry to the seen set doesn't
  // retrigger the effect. Earlier version put `animatedIds` in the dep
  // array; the resulting cleanup torn down the just-scheduled
  // setTimeouts before they could clear `active`, leaving glitch /
  // poison element overlays painted on the widget forever.
  const animatedIdsRef = useRef<Set<string>>(new Set());
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const [active, setActive] = useState<WildFxEntry[]>([]);

  useEffect(() => {
    const fresh = recentFx.filter((fx) => !animatedIdsRef.current.has(fx.id));
    if (fresh.length === 0) return;
    for (const fx of fresh) animatedIdsRef.current.add(fx.id);
    setActive((prev) => [...prev, ...fresh]);
    for (const fx of fresh) {
      if (onShake && fx.outcome === 'hit') {
        const meta = ELEMENT_FX[fx.element] ?? ELEMENT_FX.hype!;
        if (meta.shake !== 'none') onShake(meta.shake);
      }
      // Pass 28ai — audio cue on miss / dodge so the floating MISS /
      // DODGE stamp doesn't fly silently across the canvas. Hit
      // sounds remain owned by each widget's own snapshot diff path
      // (wild_attack on HP decrement) so we don't double-fire here.
      if (fx.outcome === 'miss' || fx.outcome === 'dodge') {
        void gameSounds.play('wild_miss');
      }
      // Pass 28ap — heal sound for group_heal landings AND each
      // group_hot tick (engine emits a fresh fx entry per tick).
      if (
        fx.outcome === 'support' &&
        (fx.supportKind === 'group_heal' || fx.supportKind === 'group_hot')
      ) {
        void gameSounds.play('wild_heal');
      }
      // Pass 28as — catch_fail audio cue matches the centered stamp.
      if (fx.outcome === 'catch_fail') {
        void gameSounds.play('wild_catch_fail');
      }
      const t = setTimeout(() => {
        setActive((prev) => prev.filter((e) => e.id !== fx.id));
      }, 1200);
      timersRef.current.push(t);
    }
  }, [recentFx, onShake]);

  // Single unmount cleanup. Don't clear timers between recentFx
  // updates — that's what caused the lingering overlay bug.
  useEffect(() => {
    return () => {
      for (const t of timersRef.current) clearTimeout(t);
      timersRef.current = [];
    };
  }, []);

  if (active.length === 0) return null;
  return (
    <>
      <style>{WILD_FX_KEYFRAMES}</style>
      {active.map((fx) => (
        <WildAttackFx
          key={fx.id}
          fx={fx}
          enableCenterShake={enableCenterShake}
          enableCenterStamp={enableCenterStamp}
        />
      ))}
    </>
  );
}

function WildAttackFx({
  fx,
  enableCenterShake,
  enableCenterStamp,
}: {
  fx: WildFxEntry;
  enableCenterShake: boolean;
  enableCenterStamp: boolean;
}) {
  const meta = ELEMENT_FX[fx.element] ?? ELEMENT_FX.hype!;
  const isHit = fx.outcome === 'hit';
  const isMiss = fx.outcome === 'miss';
  const isDodge = fx.outcome === 'dodge';
  const isSupport = fx.outcome === 'support';
  const isCatchFail = fx.outcome === 'catch_fail';
  // Pass 28ap — support stamps use the element's flash + particle
  // palette but a distinct color + label per supportKind. DoT is the
  // exception: it acts like a hit (deals real damage to the wild) so
  // it reuses the red damage stamp with a "DoT" tag.
  const supportLabel = (() => {
    switch (fx.supportKind) {
      case 'dot':
        return `${fx.attackEmoji} ${fx.damage} DoT`;
      case 'stun':
        return `${fx.attackEmoji} STUNNED`;
      case 'group_heal':
        return `${fx.attackEmoji} GROUP HEAL`;
      case 'group_hot':
        return `${fx.attackEmoji} +HP`;
      case 'group_buff':
        return `${fx.attackEmoji} BUFF`;
      default:
        return `${fx.attackEmoji} SUPPORT`;
    }
  })();
  const supportColor =
    fx.supportKind === 'dot'
      ? '#f87171'
      : fx.supportKind === 'stun'
        ? '#a5b4fc'
        : fx.supportKind === 'group_heal' || fx.supportKind === 'group_hot'
          ? '#34d399'
          : '#fde047'; // group_buff

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 1100,
        overflow: 'hidden',
      }}
    >
      {(isHit || isSupport) && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: meta.flash,
            mixBlendMode: fx.element === 'shadow' ? 'multiply' : 'screen',
            animation: 'wildFxFlash 1.2s ease-out forwards',
            opacity: isSupport ? 0.6 : 1,
          }}
        />
      )}
      {isHit && fx.element === 'electric' && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(255,255,255,0.6)',
            animation: 'wildFxStrobe 1.2s ease-out forwards',
          }}
        />
      )}
      {isHit && fx.element === 'glitch' && (
        <>
          {[20, 50, 78].map((top, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: 0,
                top: `${top}%`,
                width: '100%',
                height: 18,
                background: 'rgba(168,85,247,0.55)',
                mixBlendMode: 'screen',
                animation: `wildFxGlitchSlide ${0.18 + i * 0.05}s linear infinite`,
              }}
            />
          ))}
        </>
      )}
      {(isHit || isSupport) && meta.particles.length > 0 && (
        <WildFxParticles
          particles={meta.particles}
          dir={meta.particleDir}
          count={fx.element === 'ice' ? 28 : isSupport ? 8 : 14}
        />
      )}
      {isHit && enableCenterShake && meta.shake !== 'none' && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: 720,
            height: 720,
            border: '4px solid transparent',
            animation: `wildFx${meta.shake === 'shakeHard' ? 'ShakeHard' : 'Shake'} 0.45s linear`,
          }}
        />
      )}
      {enableCenterStamp && (
        <div
          style={{
            position: 'absolute',
            top: '38%',
            left: '50%',
            color: isCatchFail
              ? '#fb7185'
              : isSupport
                ? supportColor
                : isMiss
                  ? '#f97316'
                  : isDodge
                    ? '#38bdf8'
                    : fx.effectiveness === 'super'
                      ? '#fbbf24'
                      : fx.effectiveness === 'resisted'
                        ? '#94a3b8'
                        : '#f87171',
            fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
            fontWeight: 900,
            fontSize: isHit ? 72 : 56,
            letterSpacing: 2,
            textShadow:
              '0 4px 8px rgba(0,0,0,0.9), 0 0 32px ' +
              (isCatchFail
                ? 'rgba(251,113,133,0.7)'
                : isSupport
                  ? `${supportColor}cc`
                  : isMiss
                    ? 'rgba(249,115,22,0.6)'
                    : isDodge
                      ? 'rgba(56,189,248,0.6)'
                      : 'rgba(0,0,0,0.6)'),
            animation: 'wildFxBlinkMiss 1.2s ease-out forwards',
            pointerEvents: 'none',
          }}
        >
          {isCatchFail
            ? `💔 ${fx.attackerViewer} MISSED CATCH`
            : isSupport
              ? supportLabel
              : isMiss
                ? `${fx.attackEmoji} MISS`
                : isDodge
                  ? `💨 DODGE`
                  : `${fx.attackEmoji} ${fx.damage}${fx.effectiveness === 'super' ? ' ✨' : ''}`}
        </div>
      )}
    </div>
  );
}

function WildFxParticles({
  particles,
  dir,
  count,
}: {
  particles: string[];
  dir: 'fall' | 'rise' | 'streak' | 'swirl' | 'ripple' | 'none';
  count: number;
}) {
  if (dir === 'none') return null;
  const items = Array.from({ length: count }, (_, i) => i);
  return (
    <>
      {items.map((i) => {
        const p = particles[i % particles.length]!;
        const left = Math.random() * 100;
        const top = Math.random() * 100;
        const delay = Math.random() * 0.25;
        const dur = 0.9 + Math.random() * 0.45;
        const size = 24 + Math.random() * 28;
        let animation = '';
        let posStyle: React.CSSProperties = {};
        if (dir === 'fall') {
          animation = `wildFxFallDown ${dur}s linear ${delay}s forwards`;
          posStyle = { left: `${left}%`, top: 0 };
        } else if (dir === 'rise') {
          animation = `wildFxRiseUp ${dur}s ease-out ${delay}s forwards`;
          posStyle = { left: `${left}%`, bottom: 0 };
        } else if (dir === 'streak') {
          animation = `wildFxStreak ${dur}s linear ${delay}s forwards`;
          posStyle = { left: 0, top: `${top}%` };
        } else if (dir === 'ripple') {
          animation = `wildFxRipple ${dur}s ease-out ${delay}s forwards`;
          posStyle = { left: '50%', top: '50%' };
        } else if (dir === 'swirl') {
          animation = `wildFxSwirl ${dur}s ease-in-out ${delay}s forwards`;
          posStyle = { left: `${left}%`, top: `${top}%` };
        }
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              fontSize: size,
              ...posStyle,
              animation,
              pointerEvents: 'none',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))',
            }}
          >
            {p}
          </div>
        );
      })}
    </>
  );
}
